import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useBilling } from "../context/BillingContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Loader2, Save, Music, Crown, Unplug, Check, ArrowRight } from "lucide-react";
import { isNativeApp } from "../lib/platform";
import MacroSplitEditor from "../components/MacroSplitEditor";
import BadgeStrip from "../components/BadgeStrip";

const EQUIPMENT_OPTIONS = ["bodyweight", "dumbbells", "barbell", "machines", "kettlebell", "resistance bands", "pull-up bar"];

export default function ProfilePage() {
  const { user, setUser } = useAuth();
  const { sub } = useBilling();
  const [form, setForm] = useState({
    name: user?.name || "",
    age: user?.age || "",
    sex: user?.sex || "",
    height_cm: user?.height_cm || "",
    weight_kg: user?.weight_kg || "",
    goal: user?.goal || "",
    fitness_level: user?.fitness_level || "",
    days_per_week: user?.days_per_week || "",
    daily_calorie_target: user?.daily_calorie_target || "",
    equipment: user?.equipment || [],
    album_accent_enabled: user?.album_accent_enabled !== false,
  });
  const [saving, setSaving] = useState(false);
  const [spotify, setSpotify] = useState({ loaded: false, connected: false });

  const loadSpotify = async () => {
    try {
      const { data } = await api.get("/spotify/status");
      setSpotify({ ...data, loaded: true });
    } catch {
      setSpotify({ loaded: true, configured: false, connected: false });
    }
  };

  useEffect(() => {
    loadSpotify();
    const onMsg = (e) => {
      if (e.data?.type === "spotify-connected") loadSpotify();
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  const connectSpotify = async () => {
    try {
      const { data } = await api.get("/spotify/login");
      const w = window.open(data.auth_url, "spotify-oauth", "width=520,height=720");
      if (!w) window.location.href = data.auth_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start Spotify login");
    }
  };

  const disconnectSpotify = async () => {
    if (!window.confirm("Disconnect Spotify?")) return;
    try {
      await api.delete("/spotify/disconnect");
      toast.success("Spotify disconnected");
      loadSpotify();
    } catch {
      toast.error("Failed to disconnect");
    }
  };

  const toggleEquip = (eq) =>
    setForm((f) => ({
      ...f,
      equipment: f.equipment.includes(eq) ? f.equipment.filter((e) => e !== eq) : [...f.equipment, eq],
    }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      ["age", "height_cm", "weight_kg", "days_per_week", "daily_calorie_target"].forEach((k) => {
        if (payload[k] === "" || payload[k] == null) payload[k] = null;
        else payload[k] = Number(payload[k]);
      });
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "" || payload[k] === null) delete payload[k];
      });
      const { data } = await api.put("/auth/profile", payload);
      setUser(data);
      localStorage.setItem("if_user", JSON.stringify(data));
      toast.success("Profile saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const fld = (key, label, props = {}) => (
    <div>
      <label className="label-eyebrow text-white/50 block mb-2">{label}</label>
      <input
        value={form[key] ?? ""}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="w-full bg-[#1A1A1A] border border-white/10 px-4 py-3 focus:outline-none focus:border-[#6B9BFF]"
        data-testid={`profile-${key}`}
        {...props}
      />
    </div>
  );

  return (
    <div className="p-6 lg:p-10 max-w-4xl mx-auto fade-up" data-testid="profile-page">
      <div className="mb-10">
        <div className="label-eyebrow text-[#6B9BFF] mb-2">Profile</div>
        <h1 className="font-display text-4xl sm:text-5xl leading-none">Your stats</h1>
        <p className="text-white/60 mt-3">More accurate profile = better AI plans.</p>
      </div>

      {/* Subscription summary */}
      {sub && (
        <div
          className="mb-8 border border-white/10 bg-[#121212] p-5 flex flex-wrap items-center gap-4"
          data-testid="profile-billing-card"
        >
          <div className={`w-10 h-10 flex items-center justify-center border shrink-0 ${
            sub.status === "active"   ? "border-[#34C759]/40 text-[#34C759]" :
            sub.status === "trialing" ? "border-[#6B9BFF]/40 text-[#6B9BFF]" :
                                        "border-[#FFB020]/40 text-[#FFB020]"
          }`}>
            <Crown className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="label-eyebrow text-white/50 text-[10px] mb-0.5">Subscription</div>
            <div className="text-sm font-bold">
              {sub.status === "active"   && `Pro · renews ${new Date(sub.access_end).toLocaleDateString("en-AU", { month: "short", day: "numeric", year: "numeric" })}`}
              {sub.status === "trialing" && `Free trial · ${sub.days_remaining} day${sub.days_remaining === 1 ? "" : "s"} left`}
              {sub.status === "free"     && "Free plan · AI features locked"}
            </div>
            <div className="text-xs text-white/50 mt-0.5">
              ${sub.plan?.amount?.toFixed(2)} {sub.plan?.currency?.toUpperCase()} / month after trial
            </div>
          </div>
          <Link
            to="/app/billing"
            className="bg-[#6B9BFF] text-black font-bold uppercase tracking-widest text-xs px-4 py-2.5 hover:bg-white inline-flex items-center gap-1.5"
            data-testid="profile-billing-cta"
          >
            {isNativeApp() ? "View" : "Manage"} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      )}

      <form onSubmit={save} className="space-y-6" data-testid="profile-form">
        <div className="border border-white/10 bg-[#121212] p-6">
          <div className="label-eyebrow text-white/50 mb-4">Basics</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fld("name", "Name", { required: true })}
            {fld("age", "Age", { type: "number", min: 10, max: 100 })}
            <div>
              <label className="label-eyebrow text-white/50 block mb-2">Sex</label>
              <select
                value={form.sex || ""}
                onChange={(e) => setForm({ ...form, sex: e.target.value })}
                className="w-full bg-[#1A1A1A] border border-white/10 px-4 py-3 focus:outline-none focus:border-[#6B9BFF]"
                data-testid="profile-sex"
              >
                <option value="">—</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
            {fld("height_cm", "Height (cm)", { type: "number", step: 0.1 })}
            {fld("weight_kg", "Weight (kg)", { type: "number", step: 0.1 })}
          </div>
        </div>

        <div className="border border-white/10 bg-[#121212] p-6">
          <div className="label-eyebrow text-white/50 mb-4">Training</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label-eyebrow text-white/50 block mb-2">Goal</label>
              <select
                value={form.goal || ""}
                onChange={(e) => setForm({ ...form, goal: e.target.value })}
                className="w-full bg-[#1A1A1A] border border-white/10 px-4 py-3 focus:outline-none focus:border-[#6B9BFF]"
                data-testid="profile-goal"
              >
                <option value="">—</option>
                <option value="lose_fat">Lose fat</option>
                <option value="build_muscle">Build muscle</option>
                <option value="maintain">Maintain</option>
                <option value="endurance">Endurance</option>
              </select>
            </div>
            <div>
              <label className="label-eyebrow text-white/50 block mb-2">Fitness level</label>
              <select
                value={form.fitness_level || ""}
                onChange={(e) => setForm({ ...form, fitness_level: e.target.value })}
                className="w-full bg-[#1A1A1A] border border-white/10 px-4 py-3 focus:outline-none focus:border-[#6B9BFF]"
                data-testid="profile-level"
              >
                <option value="">—</option>
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>
            {fld("days_per_week", "Days per week", { type: "number", min: 1, max: 7 })}
            {fld("daily_calorie_target", "Daily calorie target", { type: "number" })}
            <div className="md:col-span-2">
              <label className="label-eyebrow text-white/50 block mb-2">Equipment</label>
              <div className="flex flex-wrap gap-2">
                {EQUIPMENT_OPTIONS.map((eq) => (
                  <button
                    key={eq}
                    type="button"
                    onClick={() => toggleEquip(eq)}
                    className={`px-3 py-2 text-xs uppercase tracking-wider border transition-colors ${
                      form.equipment.includes(eq)
                        ? "border-[#6B9BFF] text-[#6B9BFF] bg-[#6B9BFF]/5"
                        : "border-white/10 text-white/60 hover:border-white/30"
                    }`}
                    data-testid={`profile-equip-${eq.replace(/\s/g, "-")}`}
                  >
                    {eq}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mb-6" data-testid="profile-badges-section">
          <BadgeStrip />
        </div>

        <div className="mb-6" data-testid="profile-macro-split-section">
          <div className="flex items-center justify-between mb-3">
            <div className="label-eyebrow text-white/50">Macro split</div>
            <div className="text-[10px] text-white/40 uppercase tracking-widest">
              Per-user override
            </div>
          </div>
          <MacroSplitEditor
            kcalTarget={Number(form.daily_calorie_target) || user?.daily_calorie_target || 2200}
          />
        </div>

        <div className="border border-white/10 bg-[#121212] p-6 mb-6" data-testid="profile-album-accent-card">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
            <div className="flex items-center gap-2">
              <span
                className="w-3.5 h-3.5 rounded-full ring-2 ring-white/20"
                style={{ background: "linear-gradient(135deg, #6B9BFF, #B76E79)" }}
                aria-hidden
              />
              <div className="label-eyebrow text-white/50">Album-art accent</div>
            </div>
            <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="profile-album-accent-toggle">
              <span className="text-xs text-white/60">
                {form.album_accent_enabled === false ? "Off" : "On"}
              </span>
              <input
                type="checkbox"
                checked={form.album_accent_enabled !== false}
                onChange={async (e) => {
                  const next = e.target.checked;
                  setForm((f) => ({ ...f, album_accent_enabled: next }));
                  try {
                    const { data } = await api.put("/auth/profile", { album_accent_enabled: next });
                    setUser(data);
                    localStorage.setItem("if_user", JSON.stringify(data));
                  } catch (err) {
                    // Form state is already optimistic — a failed PUT just
                    // means the toggle won't survive a refresh. Log so we
                    // can spot recurring API errors in browser devtools.
                    console.warn("Album accent toggle PUT failed", err);
                  }
                }}
                className="w-9 h-5 appearance-none rounded-full bg-white/10 checked:bg-[#6B9BFF] relative cursor-pointer transition-colors before:content-[''] before:absolute before:top-0.5 before:left-0.5 before:w-4 before:h-4 before:rounded-full before:bg-white before:transition-transform checked:before:translate-x-4"
              />
            </label>
          </div>
          <p className="text-xs text-white/50">
            When music is playing, the app&apos;s accent tints to match the current album cover.
            Disable for the steady DAIONE blue.
          </p>
        </div>

        <div className="border border-white/10 bg-[#121212] p-6 mb-6" data-testid="profile-spotify-card">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <div className="flex items-center gap-2">
              <Music className="w-4 h-4 text-[#1DB954]" />
              <div className="label-eyebrow text-white/50">Spotify integration</div>
            </div>
            {spotify.connected && (
              <span className="text-[10px] uppercase tracking-widest font-bold text-[#1DB954] border border-[#1DB954]/40 bg-[#1DB954]/5 px-2 py-1 inline-flex items-center gap-1">
                <Check className="w-3 h-3" /> Connected
              </span>
            )}
          </div>
          {!spotify.loaded ? (
            <div className="text-sm text-white/40 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>
          ) : !spotify.configured ? (
            <div className="text-sm text-white/60">
              Admin needs to set <code className="text-[#6B9BFF]">SPOTIFY_CLIENT_ID</code> in backend env first.
            </div>
          ) : !spotify.connected ? (
            <div>
              <p className="text-sm text-white/60 mb-4">
                Link your Spotify to browse playlists, liked songs and top tracks from the dedicated <Link to="/app/music" className="text-[#6B9BFF] underline">Music page</Link>.
              </p>
              <button
                type="button"
                onClick={connectSpotify}
                className="bg-[#1DB954] text-black font-bold uppercase tracking-wider text-xs px-5 py-3 hover:bg-white inline-flex items-center gap-2"
                data-testid="profile-spotify-connect"
              >
                <Music className="w-4 h-4" /> Connect Spotify
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                {spotify.profile?.images?.[0]?.url && (
                  <img src={spotify.profile.images[0].url} alt="" className="w-12 h-12 rounded-full" />
                )}
                <div>
                  <div className="font-bold text-sm">{spotify.profile?.display_name || "Spotify user"}</div>
                  <div className="text-xs text-white/50 mt-0.5 flex items-center gap-1">
                    {spotify.premium ? (
                      <><Crown className="w-3 h-3 text-[#FFB020]" /> Premium</>
                    ) : (
                      "Free account"
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Link
                  to="/app/music"
                  className="border border-white/15 hover:border-[#6B9BFF] hover:text-[#6B9BFF] text-xs uppercase tracking-wider font-bold px-4 py-2"
                  data-testid="profile-spotify-open"
                >
                  Open Music
                </Link>
                <button
                  type="button"
                  onClick={disconnectSpotify}
                  className="text-white/40 hover:text-[#FF3B30] text-xs uppercase tracking-wider font-bold px-3 py-2 inline-flex items-center gap-1"
                  data-testid="profile-spotify-disconnect"
                >
                  <Unplug className="w-3.5 h-3.5" /> Disconnect
                </button>
              </div>
            </div>
          )}
        </div>

        <button
          disabled={saving}
          type="submit"
          className="bg-[#6B9BFF] text-black font-black uppercase tracking-widest px-8 py-4 hover:bg-white transition-colors inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="profile-save"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save profile
        </button>
      </form>
    </div>
  );
}
